import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RECOVERY_BOARD_SPACES } from "@/data/recovery-board";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "@/hooks/useAuthUser";

export const RECOVERY_BOARD_KEY = "rt-recovery-board-v1";
export type RecoveryBoardProgress = { pos: number; tokens: number; visited: number[] };
const INITIAL: RecoveryBoardProgress = { pos: 0, tokens: 0, visited: [0] };
type Backup = { progress: RecoveryBoardProgress; pending: boolean; updatedAt: string };
function valid(saved: RecoveryBoardProgress): boolean {
  return !!saved && Number.isInteger(saved.pos) && saved.pos >= 0 && saved.pos < RECOVERY_BOARD_SPACES.length &&
    Number.isInteger(saved.tokens) && saved.tokens >= 0 && saved.tokens <= 2147483647 &&
    Array.isArray(saved.visited) && saved.visited.every(i => Number.isInteger(i) && i >= 0 && i < 24);
}
function readBackup(uid: string): Backup | null {
  try {
    const raw = localStorage.getItem(`${RECOVERY_BOARD_KEY}:${uid}`);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && valid(saved.progress) ? saved : null;
  } catch { return null; }
}
function writeBackup(uid: string, progress: RecoveryBoardProgress, pending: boolean, updatedAt: string) {
  try { localStorage.setItem(`${RECOVERY_BOARD_KEY}:${uid}`, JSON.stringify({ progress, pending, updatedAt })); } catch { /* Account saving works without browser storage. */ }
}
async function upload(uid: string, progress: RecoveryBoardProgress) {
  const { data, error } = await supabase.from("recovery_board_progress")
    .upsert({ user_id: uid, ...progress }, { onConflict: "user_id" }).select("updated_at").single();
  if (error) throw error;
  writeBackup(uid, progress, false, data.updated_at);
  return progress;
}
export function useRecoveryBoardProgress() {
  const uid = useAuthUser()?.id;
  const client = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const query = useQuery({
    queryKey: ["recovery-board-progress", uid], enabled: !!uid, staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!uid) return INITIAL;
      const { data, error } = await supabase.from("recovery_board_progress")
        .select("pos,tokens,visited,updated_at").eq("user_id", uid).maybeSingle();
      if (error) throw error; // A failed read must never overwrite the account save.
      const backup = readBackup(uid);
      if (backup?.pending && (!data || Date.parse(backup.updatedAt) > Date.parse(data.updated_at))) {
        return upload(uid, backup.progress);
      }
      if (data) {
        const progress = { pos: data.pos, tokens: data.tokens, visited: data.visited };
        writeBackup(uid, progress, false, data.updated_at);
        return progress;
      }
      // Import a legacy browser save once, only when the account has no saved game.
      let progress = backup?.progress ?? INITIAL;
      try {
        const raw = localStorage.getItem(RECOVERY_BOARD_KEY);
        if (!backup && raw) {
          const legacy = JSON.parse(raw);
          if (valid(legacy)) progress = legacy;
        }
      } catch { /* Invalid legacy saves are ignored. */ }
      const { error: insertError } = await supabase.from("recovery_board_progress").insert({ user_id: uid, ...progress });
      if (insertError) {
        if (insertError.code !== "23505") throw insertError;
        const { data: existing, error: readError } = await supabase.from("recovery_board_progress")
          .select("pos,tokens,visited").eq("user_id", uid).single();
        if (readError) throw readError;
        progress = existing;
      }
      writeBackup(uid, progress, false, new Date().toISOString());
      try { localStorage.removeItem(RECOVERY_BOARD_KEY); } catch { /* Optional cleanup. */ }
      return progress;
    },
  });
  const saveProgress = useCallback((progress: RecoveryBoardProgress) => {
    if (!uid || !valid(progress)) return;
    writeBackup(uid, progress, true, new Date().toISOString());
    client.setQueryData(["recovery-board-progress", uid], progress);
    setSaving(true);
    setSaveError(false);
    queue.current = queue.current.catch(() => undefined).then(async () => {
      await client.cancelQueries({ queryKey: ["recovery-board-progress", uid] });
      await upload(uid, progress);
    });
    const current = queue.current;
    void current.then(() => { if (queue.current === current) setSaving(false); })
      .catch(() => { if (queue.current === current) { setSaving(false); setSaveError(true); } });
  }, [uid, client]);
  const retrySave = useCallback(() => {
    if (!uid) return;
    const backup = readBackup(uid);
    if (backup?.pending) saveProgress(backup.progress);
    else void query.refetch();
  }, [uid, saveProgress, query.refetch]);
  return { ...(query.data ?? INITIAL), loading: !uid || query.isPending, error: query.isError || saveError,
    saving, saveProgress, retrySave };
}
